import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/data-grid-1-wrapper';
import * as React from 'react';
import * as _ from 'underscore';

import { DataConnectionsGrid } from '../../app/content/DataConnectionsGrid';
import { AngularDirective } from '../../common/react/components/AngularDirective';
import {
  AngularToReactDataGridBridge,
  IDataGridPropsScope,
} from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { AngularContext } from '../../ts/factories/AngularContextFactory';
import { ActionButtonsHelper, IActionsMenuScope } from '../../ts/helpers/ActionButtonsHelper';
import { DataConnectionInFlowContentType } from '../../ts/models/ContentTypes';
import { FlowDetailInfo } from '../../ts/models/FlowInfo';
import { FlowOutputStepRunListItemInfo } from '../../ts/models/FlowOutputStepRunInfo';
import { IDataConnectionInfo } from '../../ts/models/IDataConnectionInfo';
import { IEmptyTemplateModel } from '../../ts/models/IEmptyTemplate';
import { ISubplace } from '../../ts/models/IPlace';
import { ISliceable } from '../../ts/models/ISliceable';
import { Set } from '../../ts/models/Set';
import { FlowAllowedActions } from '../../ts/ServerApi/types/FlowAllowedActions';
import { FlowInputStep } from '../../ts/ServerApi/types/FlowInputStep';
import { FlowOutputStep } from '../../ts/ServerApi/types/FlowOutputStep';
import { ActionPanelService } from '../../ts/services/ActionPanelService';
import { IQueryUpdater, QueryService } from '../../revisionHistory/services/QueryService';
import { ServerService } from '../../ts/services/ServerService';
import { Sorter } from '../../content/Sorter';

type FlowConnectionsDataGridViewScope =
  DataConnectionsGrid.DataConnectionsGridScope &
  IActionsMenuScope<IDataConnectionInfo, {}> &
  IDataGridPropsScope<IDataConnectionInfo> & {
    // This is used by the selection menu.
    sliceable: ISliceable<IDataConnectionInfo>;
  };

const template = `<div ng-include="'dataGridSubplaceWithoutFilterPanel.html'"></div>`;

const connectionsEmptyTemplateModel: IEmptyTemplateModel = {
  title: 'flowsEmptyConnections',
};

export type SelectedConnections = { readonly [connectionId: string]: IDataConnectionInfo };

export type FlowConnectionsDataGridViewProps = {
  readonly angularContext: AngularContext;
  readonly flowAllowedActions: FlowAllowedActions;
  readonly flowDetailInfo: FlowDetailInfo;
  readonly mostRecentRuns: ReadonlyArray<FlowOutputStepRunListItemInfo> | null;
  readonly inputSteps: ReadonlyArray<FlowInputStep> | null;
  readonly outputSteps: ReadonlyArray<FlowOutputStep> | null;
  readonly siteUrls: SiteUrls;
  readonly subplace: ISubplace<IDataConnectionInfo>;
  readonly translate: IReactTranslateService;
  readonly selectedConnections: SelectedConnections;
  readonly setSelectedConnections: (selectedConnections: SelectedConnections) => void;
};

export class FlowConnectionsDataGridView extends React.Component<FlowConnectionsDataGridViewProps> {

  private actionPanelService: ActionPanelService;
  private angularToReactDataGridBridge: AngularToReactDataGridBridge;
  private queryService: QueryService;
  private serverService: ServerService;
  private scope: FlowConnectionsDataGridViewScope;

  constructor(props: FlowConnectionsDataGridViewProps) {
    super(props);
    const angularContext = this.props.angularContext;
    this.actionPanelService = angularContext.getService('ActionPanelService');
    this.angularToReactDataGridBridge = angularContext.getService('AngularToReactDataGridBridge');
    this.queryService = angularContext.getService('QueryService');
    this.serverService = angularContext.getService('ServerService');
  }

  public render(): JSX.Element {
    return (
      <AngularDirective
        angularContext={this.props.angularContext}
        setupScope={this.setupScope}
        template={template}
      />
    );
  }

  public componentDidUpdate(prevProps: Readonly<FlowConnectionsDataGridViewProps>) {
    if (this.props.selectedConnections !== prevProps.selectedConnections) {
      this.scope.$applyAsync(() => {
        this.scope.selectedItems.clear();
        for (const connectionId in this.props.selectedConnections) {
          const connection = this.props.selectedConnections[connectionId];
          this.scope.selectedItems.add(connection);
        }
      });
    }

    if (this.props.flowDetailInfo.id !== prevProps.flowDetailInfo.id ||
        this.props.inputSteps !== prevProps.inputSteps ||
        this.props.outputSteps !== prevProps.outputSteps ||
        this.props.mostRecentRuns !== prevProps.mostRecentRuns
    ) {
      this.scope.dataGridProps.cols = this.createColumns();
    }

    if (this.props.flowDetailInfo.id !== prevProps.flowDetailInfo.id) {
      this.createQueryUpdater();
    }
  }

  private createColumns(): DataGrid.Column[] {
    return DataConnectionsGrid.columnsForFlow(
      this.actionPanelService.getDataConnectionActions(),
      this.anyActionsAllowed(),
      this.props.flowDetailInfo.id,
      this.props.flowAllowedActions,
      ActionButtonsHelper.getActionTargetFn(this.scope, this.scope.selectedItems),
      () => this.props.mostRecentRuns,
      () => this.props.inputSteps,
      () => this.props.outputSteps,
      this.scope,
      this.props.siteUrls,
      this.props.translate,
    );
  }

  private readonly setupScope = (scope: ng.IScope): ng.IScope => {
    this.scope = scope.$new() as FlowConnectionsDataGridViewScope;

    this.scope.selectedItems = new Set<IDataConnectionInfo>(Set.PluckId);
    this.scope.$watch(() => {
      // The logic below is copied from AngularToReactDataGridBridge.
      // Maybe instead of copying we can add a hook?
      if (!this.scope.selectedItems || this.scope.selectedItems.size() === 0) {
        return 'none';
      }
      return this.scope.selectedItems.keys().join();
    }, this.handleSelectionUpdate);

    const actions = this.actionPanelService.getDataConnectionActions();
    const getActionTarget = ActionButtonsHelper.getActionTargetFn(this.scope, this.scope.selectedItems);
    ActionButtonsHelper.setupScopeForActionsMenuInActionBar({
      scope: this.scope,
      isActionAvailable: action => action.isAvailable(this.scope.selectedItems),
      menuActions: actions,
      getActionTarget: getActionTarget,
      translate: this.props.translate
    });
    this.configureForReactDataGrid(this.scope);

    this.scope.sorter = new Sorter(DataConnectionInFlowContentType.defaultOrder);
    this.scope.$on('$destroy', () => this.scope.sorter.detach());

    this.createQueryUpdater();

    return this.scope;
  }

  private readonly handleSelectionUpdate = () => {
    const selectedConnections: { [connectionId: string]: IDataConnectionInfo } = {};
    this.scope.selectedItems.each((connection) => {
      selectedConnections[connection.id] = connection;
    });
    if (!_.isEqual(selectedConnections, this.props.selectedConnections)) {
      this.props.setSelectedConnections(selectedConnections);
    }
  }

  private anyActionsAllowed(): boolean {
    return this.serverService.isSessionSiteOrServerAdmin() ||
      (this.props.flowDetailInfo && this.props.flowDetailInfo.owner &&
      this.serverService.sessionUserId() === this.props.flowDetailInfo.owner.id);
  }

  private configureForReactDataGrid(scope: FlowConnectionsDataGridViewScope) {
    this.angularToReactDataGridBridge.attachReactDataGridToScope({
      $scope: scope,
      columns: this.createColumns(),
      emptyChild: this.angularToReactDataGridBridge.emptyTemplateElementFor(connectionsEmptyTemplateModel),
      canShowFilterPanel: DataConnectionInFlowContentType.canShowFilterPanel,
    });
  }

  private createQueryUpdater() {
    const queryUpdater: IQueryUpdater =
      this.queryService.createQueryUpdaterFromSubplace(
        this.scope,
        this.props.subplace,
        (resourceQuery) => {
          this.angularToReactDataGridBridge.updateReactDataGridRowStore({
            scope: this.scope,
            sliceable: resourceQuery
          });
          this.scope.sliceable = resourceQuery;
          this.scope.sorter.setOrder(resourceQuery.getOrder());
        }
      );

    queryUpdater.refresh();
  }

}
